import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { contactSchema } from '../../../validation/contact/index';
import type { ContactType } from '../../../validation/contact/contactType';

import {
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomRadioButton from '../../uiParts/CustomRadioButton';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import { checkLoginStatus } from '../../../contexts/auth';
import { getCustomerInfo, sendContact } from '../../../api/db/user';
import { useNavigate } from 'react-router-dom';
import { radioOptions } from '../../../constants/SelectOptions';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (max-width:600px)': {
      m: `${spHeaderHeight} auto 0`,
      p: '24px 0 140px',
      width: '90%'
    },
    m: `${headerHeight} auto 0`,
    p: '32px 0 200px',
    width: '560px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const Contact: React.FC = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue
  } = useForm<ContactType>({
    resolver: yupResolver(contactSchema)
  });

  const navigate = useNavigate();

  const buttonDisabledFlag = (): boolean => {
    return (
      watch('company') === undefined ||
      '' ||
      watch('type') === undefined ||
      '' ||
      watch('content') === undefined ||
      '' ||
      watch('department') === undefined ||
      '' ||
      watch('email') === undefined ||
      '' ||
      watch('name') === undefined ||
      '' ||
      watch('tel') === undefined ||
      '' ||
      Boolean(errors.company) ||
      Boolean(errors.type) ||
      Boolean(errors.content) ||
      Boolean(errors.department) ||
      Boolean(errors.email) ||
      Boolean(errors.name) ||
      Boolean(errors.tel)
    );
  };

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (isLogin) {
        await sendContact(watch, userId);
      } else {
        await sendContact(watch);
      }
      navigate('/contact/complete', {
        state: {
          isLogin: isLogin
        }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      setErrorText(error.message);
      setOpen(true);
      throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>('');

  const setValuesIfLoggedIn = async (): Promise<void> => {
    setLoading(true);
    try {
      const loginStatus = await checkLoginStatus();
      setIsLogin(loginStatus.check);
      if (loginStatus) {
        const userInfo = await getCustomerInfo();
        setValue('company', userInfo.data.company);
        setValue('department', userInfo.data.department);
        setValue(
          'name',
          `${userInfo.data.last_name} ${userInfo.data.first_name}`
        );
        setValue('email', userInfo.data.email);
        setValue('tel', userInfo.data.tel);
        setUserId(userInfo.data.id);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.message !== '認証エラー') {
        sentryLog(error);
        setErrorText(error.message);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await setValuesIfLoggedIn();
    })();
  }, []);

  return (
    <>
      <CustomHead title="お問い合わせ | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography sx={styles.title}>お問い合わせ</Typography>
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.company)}
              formMb="24px"
              helperText={errors.company?.message}
              labelText="会社名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="company"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.department)}
              formMb="24px"
              helperText={errors.department?.message}
              labelText="部署名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="department"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.name)}
              formMb="24px"
              helperText={errors.name?.message}
              labelText="担当者名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="name"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.email)}
              formMb="24px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="email"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.tel)}
              formMb="24px"
              helperText={errors.tel?.message}
              labelText="電話番号"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="tel"
        />
        <Box sx={{ mb: '24px' }}>
          <Controller
            render={({ field }) => (
              <CustomRadioButton
                {...field}
                error={Boolean(errors.type)}
                helperText={errors.type?.message}
                labelText="お問い合わせの種類"
                options={radioOptions}
                requiredText="必須"
              />
            )}
            control={control}
            name="type"
          />
        </Box>
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.content)}
              fieldType="textareaRow5"
              formMb="40px"
              helperText={errors.content?.message}
              labelText="お問い合わせの内容"
              multiline
              placeholder="サンプルテキスト"
              requiredText="必須"
              rows={5}
            />
          )}
          control={control}
          name="content"
        />
        <Button
          disabled={buttonDisabledFlag()}
          fullWidth
          sx={[primaryButtonBackgroundColor, { height: '44px' }]}
          variant="contained"
          onClick={() => {
            void (async () => {
              await handleSubmit(async () => await onSubmit())();
            })();
          }}
        >
          <Typography color="white" component="div" sx={{ fontSize: '16px' }}>
            送信する
          </Typography>
        </Button>
      </Typography>
      <CustomSnackbar
        autoHideDuration={6000}
        open={open}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default Contact;
